import { useState, useEffect, useMemo } from 'react';
import { Heart } from 'lucide-react';
import { useWishlist } from '@context/WishlistContext';
import Header from '@components/layout/Header';
import ActivityCard from '@components/activity/ActivityCard';
import Spinner from '@components/ui/Spinner';
import EmptyState from '@components/ui/EmptyState';
import api from '@utils/api';
import { isWishlistExpiredGrace, isWishlistPastRemovalGrace } from '@utils/wishlistExpiry';
import { isActivityListingEnded } from '@utils/helpers';

const WishlistPage = () => {
  const { idsOrdered, applyFetchResults } = useWishlist();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const idsKey = useMemo(() => idsOrdered.join(','), [idsOrdered]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (idsOrdered.length === 0) {
        setActivities([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const found = [];
      const notFound = [];

      await Promise.all(
        idsOrdered.map(async (id) => {
          try {
            const { data } = await api.get(`/activities/${id}`);
            if (data?.activity) found.push(data.activity);
          } catch (err) {
            if (err.response?.status === 404) notFound.push(id);
          }
        })
      );

      if (cancelled) return;

      applyFetchResults(found, notFound);

      const byId = new Map(found.map((a) => [a._id, a]));
      const ordered = idsOrdered
        .filter((id) => !notFound.includes(id))
        .map((id) => byId.get(id))
        .filter(Boolean)
        .filter((a) => !isWishlistPastRemovalGrace(a));

      setActivities(ordered);
      setLoading(false);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [idsKey, applyFetchResults]);

  return (
    <div className="min-h-screen w-full bg-surface-50 dark:bg-surface-950">
      <Header />

      <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 pb-24 lg:pb-10 pt-3 sm:pt-5">
        <h1 className="text-xl font-bold text-surface-900 dark:text-white mb-1">Wishlist</h1>
        <p className="text-sm text-surface-500 dark:text-surface-400 mb-5">
          Vibes you save from Explore. Past events stay here for a day, then drop off automatically.
        </p>

        {loading ? (
          <Spinner className="py-16" />
        ) : activities.length === 0 ? (
          <EmptyState
            icon={Heart}
            title="No saved vibes"
            description="Tap the heart on a vibe in Explore to save it here."
          />
        ) : (
          <div className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {activities.map((activity) => (
              <ActivityCard
                key={activity._id}
                activity={activity}
                wishlistCategoryMuted={isWishlistExpiredGrace(activity)}
                explorePast={isActivityListingEnded(activity)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
